import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

const ACTION_PRESETS: { label: string; value: string }[] = [
  { label: '전체', value: '' },
  { label: '인증 (auth.)', value: 'auth.' },
  { label: '사용자 (user.)', value: 'user.' },
  { label: 'Skill (skill.)', value: 'skill.' },
  { label: 'Skill 도구 (skill.tool.)', value: 'skill.tool.' },
  { label: 'Skill 시드 (skill.seed.)', value: 'skill.seed.' },
  { label: '데이터소스 (data_source.)', value: 'data_source.' },
  { label: 'Artifact RPC (artifact.rpc.)', value: 'artifact.rpc.' },
];

interface FilterState {
  actionPreset: string;
  actionCustom: string;
  actorEmail: string;
  from: string;
  to: string;
  errorOnly: boolean;
}

const EMPTY: FilterState = {
  actionPreset: '',
  actionCustom: '',
  actorEmail: '',
  from: '',
  to: '',
  errorOnly: false,
};

function toIsoOrUndefined(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatRelative(iso: string): string {
  try {
    const d = new Date(iso).getTime();
    const diff = Date.now() - d;
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec}초 전`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}분 전`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}시간 전`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}일 전`;
    return formatDateTime(iso);
  } catch {
    return iso;
  }
}

function metaSummary(meta: Record<string, unknown>): string {
  // 오류면 error 본문이 가장 중요 — 우선 노출.
  if (meta.status === 'error' && typeof meta.error === 'string') {
    const e = meta.error;
    return e.length > 80 ? `${e.slice(0, 80)}…` : e;
  }
  const keys = Object.keys(meta);
  const k = keys[0];
  if (!k) return '—';
  if (keys.length === 1) {
    const v = meta[k];
    const vs = typeof v === 'string' ? v : JSON.stringify(v);
    return `${k}: ${vs.length > 40 ? `${vs.slice(0, 40)}…` : vs}`;
  }
  return `${keys.length}개 키`;
}

export function AdminAuditLogs() {
  // URL ?status=error&action=artifact.rpc. 같은 deep link 로 들어왔을 때 초기 필터 시드.
  const search = useSearch({ from: '/admin/audit' }) as {
    status?: 'ok' | 'error';
    action?: string;
  };
  const initial: FilterState = useMemo(
    () => ({
      ...EMPTY,
      errorOnly: search.status === 'error',
      actionCustom: search.action ?? '',
    }),
    [search.status, search.action],
  );

  const [draft, setDraft] = useState<FilterState>(initial);
  const [filters, setFilters] = useState<FilterState>(initial);

  // actorEmail debounce 300ms
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((prev) => ({ ...prev, actorEmail: draft.actorEmail }));
    }, 300);
    return () => clearTimeout(handle);
  }, [draft.actorEmail]);

  const action = useMemo(() => {
    const c = draft.actionCustom.trim();
    return c || draft.actionPreset || undefined;
  }, [draft.actionPreset, draft.actionCustom]);

  // 즉시 반영 (debounce 없음): action / from / to / preset / errorOnly
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      actionPreset: draft.actionPreset,
      actionCustom: draft.actionCustom,
      from: draft.from,
      to: draft.to,
      errorOnly: draft.errorOnly,
    }));
  }, [draft.actionPreset, draft.actionCustom, draft.from, draft.to, draft.errorOnly]);

  const queryAction = useMemo(() => {
    const c = filters.actionCustom.trim();
    return c || filters.actionPreset || undefined;
  }, [filters.actionPreset, filters.actionCustom]);

  const fromIso = useMemo(() => toIsoOrUndefined(filters.from), [filters.from]);
  const toIso = useMemo(() => toIsoOrUndefined(filters.to), [filters.to]);
  const actorEmail = filters.actorEmail.trim() || undefined;
  const statusFilter: 'error' | undefined = filters.errorOnly ? 'error' : undefined;

  const query = useInfiniteQuery({
    queryKey: ['admin', 'audit-logs', queryAction, actorEmail, fromIso, toIso, statusFilter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.adminAuditLogs({
        action: queryAction,
        actorEmail,
        from: fromIso,
        to: toIso,
        status: statusFilter,
        cursor: pageParam,
        limit: 50,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const total = query.data?.pages[0]?.total ?? 0;
  const logs = useMemo(() => query.data?.pages.flatMap((p) => p.logs) ?? [], [query.data]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
          query.fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [query]);

  function reset() {
    setDraft(EMPTY);
    setFilters(EMPTY);
  }

  return (
    <PageShell
      header={
        <>
          <PageHeader
            title="감사 로그"
            count={total}
            description="관리자 작업과 시스템 이벤트를 시간순으로 기록합니다."
            actions={
              <Button variant="secondary" onClick={() => query.refetch()}>
                새로고침
              </Button>
            }
          />
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <FilterField label="액션">
              <select
                value={draft.actionPreset}
                onChange={(e) => setDraft((s) => ({ ...s, actionPreset: e.target.value }))}
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              >
                {ACTION_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField label="액션 직접 입력 (prefix)">
              <input
                type="text"
                value={draft.actionCustom}
                onChange={(e) => setDraft((s) => ({ ...s, actionCustom: e.target.value }))}
                placeholder="예: skill."
                className="h-9 w-44 rounded-md border border-border bg-surface px-2 text-sm font-mono"
              />
            </FilterField>
            <FilterField label="액터 이메일 (substring)">
              <input
                type="text"
                value={draft.actorEmail}
                onChange={(e) => setDraft((s) => ({ ...s, actorEmail: e.target.value }))}
                placeholder="admin@..."
                className="h-9 w-56 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <FilterField label="시작">
              <input
                type="datetime-local"
                value={draft.from}
                onChange={(e) => setDraft((s) => ({ ...s, from: e.target.value }))}
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <FilterField label="끝">
              <input
                type="datetime-local"
                value={draft.to}
                onChange={(e) => setDraft((s) => ({ ...s, to: e.target.value }))}
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <FilterField label="상태">
              <label className="flex h-9 items-center gap-2 rounded-md border border-border bg-surface px-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.errorOnly}
                  onChange={(e) => setDraft((s) => ({ ...s, errorOnly: e.target.checked }))}
                  className="accent-destructive"
                />
                <span>오류만</span>
              </label>
            </FilterField>
            <Button variant="ghost" size="sm" onClick={reset}>
              초기화
            </Button>
          </div>
          {action || filters.errorOnly ? (
            <p className="mt-2 text-xs text-foreground-muted">
              적용:
              {action ? (
                <>
                  {' '}
                  action = <span className="font-mono">{action}</span>
                </>
              ) : null}
              {filters.errorOnly ? (
                <>
                  {action ? ' · ' : ' '}
                  <span className="font-mono text-destructive">status=error</span>
                </>
              ) : null}
              {actorEmail ? (
                <>
                  {' · '}email <span className="font-mono">{actorEmail}</span>
                </>
              ) : null}
              {fromIso ? <> · {formatDateTime(fromIso)} 이후</> : null}
              {toIso ? <> · {formatDateTime(toIso)} 이전</> : null}
            </p>
          ) : null}
        </>
      }
    >
      {query.isError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {query.error instanceof ApiError
            ? query.error.message
            : '감사 로그를 불러오지 못했습니다.'}
        </div>
      ) : null}

      <div className="rounded-2xl border border-border bg-surface overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
            <tr>
              <th className="text-left font-medium px-4 py-3 w-[200px]">시각</th>
              <th className="text-left font-medium px-4 py-3 w-[220px]">액션</th>
              <th className="text-left font-medium px-4 py-3 w-[240px]">액터</th>
              <th className="text-left font-medium px-4 py-3">대상</th>
              <th className="text-left font-medium px-4 py-3 w-[280px]">메타</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-foreground-muted py-10">
                  불러오는 중…
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-foreground-muted py-10">
                  조건에 맞는 로그가 없습니다.
                </td>
              </tr>
            ) : (
              logs.map((log) => {
                const isError = log.meta.status === 'error';
                return (
                  <tr
                    key={log.id}
                    className={`border-t border-border hover:bg-surface-muted/30 align-top ${
                      isError ? 'border-l-2 border-l-destructive' : ''
                    }`}
                  >
                    <td
                      className="px-4 py-3 whitespace-nowrap"
                      title={formatDateTime(log.createdAt)}
                    >
                      <div>{formatRelative(log.createdAt)}</div>
                      <div className="text-xs text-foreground-muted font-mono">
                        {formatDateTime(log.createdAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs font-mono ${
                          isError ? 'bg-destructive/10 text-destructive' : 'bg-surface-muted'
                        }`}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {log.actorUserId ? (
                        <div className="min-w-0">
                          <div className="truncate">{log.actorDisplayName ?? '—'}</div>
                          <div className="text-xs text-foreground-muted truncate">
                            {log.actorEmail ?? ''}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-foreground-muted italic">system</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs break-all">{log.target ?? '—'}</td>
                    <td className="px-4 py-3">
                      {Object.keys(log.meta).length === 0 ? (
                        <span className="text-foreground-muted">—</span>
                      ) : (
                        <details>
                          <summary
                            className={`cursor-pointer text-xs ${
                              isError ? 'text-destructive' : 'text-foreground-muted'
                            }`}
                          >
                            {metaSummary(log.meta)}
                          </summary>
                          <pre className="mt-2 font-mono text-[11px] whitespace-pre-wrap break-all bg-surface-muted/50 rounded p-2">
                            {JSON.stringify(log.meta, null, 2)}
                          </pre>
                        </details>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div
        ref={sentinelRef}
        className="h-12 flex items-center justify-center text-xs text-foreground-muted"
      >
        {query.isFetchingNextPage
          ? '더 불러오는 중…'
          : query.hasNextPage
            ? '스크롤 시 자동 로딩'
            : logs.length > 0
              ? '— 끝 —'
              : ''}
      </div>
    </PageShell>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: input 자식이 children 으로 전달되는 wrapping label 패턴 (HTML 적법)
    <label className="flex flex-col gap-1">
      <span className="text-xs text-foreground-muted">{label}</span>
      {children}
    </label>
  );
}
